import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { SERVICES_FOR_LINKS, BOROUGHS_FOR_LINKS } from '../data/landingPages';

const SITE_ORIGIN = 'https://exterminatorproservice.com';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_URL = `${BACKEND_URL}/api`;

const Stars = ({ value }) => {
  const n = Math.round(value);
  return (
    <span className="text-amber-500 text-lg" aria-label={`${value} out of 5 stars`}>
      {'★'.repeat(n)}<span className="text-gray-300">{'★'.repeat(5 - n)}</span>
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  } catch { return ''; }
};

const buildJsonLd = (reviews, stats) => {
  const blocks = [];
  // BreadcrumbList
  blocks.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: 'Customer Reviews', item: `${SITE_ORIGIN}/reviews` },
    ],
  });
  // Per-review schema
  reviews.slice(0, 20).forEach((r) => {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'Review',
      itemReviewed: {
        '@type': 'LocalBusiness',
        name: 'Exterminator ProService Connect',
        image: `${SITE_ORIGIN}/og-image.jpg`,
        telephone: '+1-646-209-9955',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2010 Seagirt Blvd',
          addressLocality: 'Queens',
          addressRegion: 'NY',
          postalCode: '11691',
          addressCountry: 'US',
        }
      },
      reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
      author: { '@type': 'Person', name: r.customer_name },
      reviewBody: r.review_text,
      datePublished: r.created_at,
    });
  });
  // Aggregate rating - only if ≥5 (Google's unofficial threshold)
  if (stats && stats.total >= 5) {
    blocks.push({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': `${SITE_ORIGIN}/#localbusiness`,
      name: 'Exterminator ProService Connect',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: stats.averageRating,
        reviewCount: stats.total,
        bestRating: 5,
        worstRating: 1,
      }
    });
  }
  return blocks;
};

export const ReviewsPage = ({ onRequestService }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/reviews`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/reviews/stats`).then(r => r.ok ? r.json() : null),
    ]).then(([rs, s]) => {
      setReviews(rs);
      setStats(s);
    }).finally(() => setLoading(false));
  }, []);

  useSEO({
    title: 'Customer Reviews | Exterminator ProService Connect NYC',
    description: stats && stats.total
      ? `Read ${stats.total} verified customer reviews of our NYC pest control services. Average rating ${stats.averageRating}/5. Bed bug, rat, cockroach, termite treatments across all 5 boroughs.`
      : 'Customer reviews of our NYC pest control services. Bed bug, rat, cockroach, and termite treatments across all 5 NYC boroughs.',
    canonicalPath: '/reviews',
    jsonLd: reviews.length ? buildJsonLd(reviews, stats) : null,
  });

  return (
    <article className="bg-white p-6 md:p-10 rounded-xl shadow-2xl border-t-8 border-amber-500" data-testid="reviews-page">
      <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
        <a href="/" className="hover:text-indigo-600">Home</a>
        <span className="mx-2">/</span>
        <span className="text-gray-700">Customer Reviews</span>
      </nav>

      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2" data-testid="reviews-h1">
        Customer Reviews
      </h1>
      <p className="text-gray-600 mb-6">What NYC homeowners and businesses say about our pest control services.</p>

      {stats && stats.total > 0 && (
        <div className="flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8" data-testid="reviews-summary">
          <div className="text-center">
            <div className="text-4xl font-extrabold text-amber-600" data-testid="reviews-avg">{stats.averageRating}</div>
            <div className="text-xs text-gray-600 uppercase tracking-wider">Average</div>
          </div>
          <div className="h-12 w-px bg-amber-200" />
          <div>
            <Stars value={stats.averageRating} />
            <div className="text-sm text-gray-700 mt-1">
              Based on <strong data-testid="reviews-total">{stats.total}</strong> customer review{stats.total === 1 ? '' : 's'}
            </div>
          </div>
        </div>
      )}

      {loading && <p className="text-center text-gray-500 py-8">Loading reviews…</p>}

      {!loading && reviews.length === 0 && (
        <div className="text-center py-10 bg-gray-50 rounded-lg">
          <p className="text-gray-600">No reviews yet. Check back soon!</p>
        </div>
      )}

      <div className="space-y-4">
        {reviews.map((r) => (
          <article key={r.id} className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition" data-testid={`review-${r.id}`}>
            <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
              <div>
                <h3 className="font-semibold text-gray-900">{r.customer_name}</h3>
                <p className="text-xs text-gray-500">
                  {r.location && <span>{r.location}</span>}
                  {r.service_type && <span> · {r.service_type}</span>}
                  {r.created_at && <span> · {formatDate(r.created_at)}</span>}
                </p>
              </div>
              <Stars value={r.rating} />
            </div>
            <p className="text-gray-700 leading-relaxed italic">"{r.review_text}"</p>
          </article>
        ))}
      </div>

      <section className="mt-10 pt-6 border-t border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Explore Services</h2>
        <div className="grid sm:grid-cols-2 gap-2 mb-6">
          {SERVICES_FOR_LINKS.map(l => (
            <a key={l.slug} href={`/${l.slug}`} className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm">→ {l.label}</a>
          ))}
        </div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Service Areas</h2>
        <div className="grid sm:grid-cols-2 gap-2">
          {BOROUGHS_FOR_LINKS.map(l => (
            <a key={l.slug} href={`/${l.slug}`} className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm">→ {l.label}</a>
          ))}
        </div>
      </section>

      <section className="mt-8 bg-gradient-to-r from-indigo-600 to-blue-700 text-white rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-2">Ready to book your service?</h2>
        <p className="mb-4 opacity-90">Call us 24/7 or request service online — free quote, no obligation.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href="tel:+16462099955" className="px-5 py-2 bg-white text-indigo-700 font-bold rounded-lg hover:bg-gray-100">
            📞 (646) 209-9955
          </a>
          <button type="button" onClick={onRequestService} className="px-5 py-2 bg-indigo-900 text-white font-bold rounded-lg hover:bg-indigo-950">
            Request Online
          </button>
        </div>
      </section>
    </article>
  );
};
